import type { SelectOption } from '@susu/ui/components'

/** ISO 3166-1 alpha-2 codes that use the state/province dropdown in the address form. */
export const ADDRESS_STATE_SELECT_COUNTRIES = ['US', 'CA'] as const

export type AddressStateSelectCountry =
  (typeof ADDRESS_STATE_SELECT_COUNTRIES)[number]

export const US_STATE_OPTIONS: SelectOption[] = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'AS', label: 'American Samoa' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'GU', label: 'Guam' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'VI', label: 'U.S. Virgin Islands' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
]

export const CA_PROVINCE_OPTIONS: SelectOption[] = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
]

const US_STATE_LABEL = new Map(US_STATE_OPTIONS.map((o) => [o.value, o.label]))
const CA_PROVINCE_LABEL = new Map(
  CA_PROVINCE_OPTIONS.map((o) => [o.value, o.label]),
)

export function countryUsesStateSelect(countryCode: string): boolean {
  const upper = countryCode.trim().toUpperCase()
  return (ADDRESS_STATE_SELECT_COUNTRIES as readonly string[]).includes(upper)
}

export function getSubdivisionOptionsForCountry(
  countryCode: string,
): SelectOption[] {
  const upper = countryCode.trim().toUpperCase()
  if (upper === 'US') return US_STATE_OPTIONS
  if (upper === 'CA') return CA_PROVINCE_OPTIONS
  return []
}

export function isKnownSubdivisionCode(
  countryCode: string,
  stateCode: string,
): boolean {
  const options = getSubdivisionOptionsForCountry(countryCode)
  return options.some((o) => o.value === stateCode.trim().toUpperCase())
}

/** Human-readable subdivision for address lines when country is US/CA. */
export function getSubdivisionDisplayLabel(
  countryCode: string,
  stateCode: string,
): string | null {
  const code = stateCode.trim().toUpperCase()
  if (!code) return null
  const upper = countryCode.trim().toUpperCase()
  if (upper === 'US') return US_STATE_LABEL.get(code) ?? null
  if (upper === 'CA') return CA_PROVINCE_LABEL.get(code) ?? null
  return null
}
